import React, { useEffect, useRef } from "react";
import Lenis from "lenis";
import "lenis/dist/lenis.css";
import { gsap, ScrollTrigger } from "./gsap";
import { prefersReducedMotion } from "./prefersReducedMotion";

interface SmoothScrollProps {
  children: React.ReactNode;
}

/**
 * Wraps the page in Lenis smooth scrolling and keeps ScrollTrigger in sync
 * with it (Lenis drives the scroll position, GSAP listens on its ticker).
 * No-op under `prefers-reduced-motion`: children render with native scroll.
 */
const SmoothScroll: React.FC<SmoothScrollProps> = ({ children }) => {
  const wrapperRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (prefersReducedMotion()) return;

    const lenis = new Lenis({
      autoRaf: false,
      // Let nested overflow:auto/scroll containers scroll natively with the
      // wheel, then hand scrolling back to the page once they hit their
      // top/bottom edge (requires overscroll-behavior:auto on the container).
      allowNestedScroll: true,
    });

    lenis.on("scroll", ScrollTrigger.update);

    const onTick = (time: number) => {
      lenis.raf(time * 1000);
    };
    gsap.ticker.add(onTick);
    gsap.ticker.lagSmoothing(0);

    return () => {
      gsap.ticker.remove(onTick);
      lenis.destroy();
    };
  }, []);

  return <div ref={wrapperRef}>{children}</div>;
};

export default SmoothScroll;
